import { useState } from 'react';
import { isEqual } from 'lodash';
import { useRouter } from 'next/router';

import {
  BrandsContentResponse,
  ProductsModel
} from '../../../interface/products';
import { useMediaQuery, MediaQuery } from '../../../hooks/useMediaQuery';
import DynamicBreadcrumbs from '../../../components/atoms/dynamic-breadcrumbs/dynamic-breadcrumbs';
import { ProductContentTabs } from '../../../enums/productTabs';
import {
  SUBZERO_API_NAME,
  SUBZERO_BRAND_NAME,
  SUBZERO_SLUG,
  WOLF_BRAND_NAME,
  WOLF_SLUG
} from '../../../constants/brands';
import ImageHoverButton from '../../../components/atoms/image-hover-button/image-hover-button';
import { linkRoutes } from '../../../config/routes/links';
import { pageRoute } from '../../../config/routes/pages';
import {
  generateProductEmailLink,
  queryToString
} from '../../../utils/products';
import ProductTabButton from '../../../components/atoms/product-tab-button/product-tab-button';
import HeadOnCarousel from '../../../components/molecules/head-on-carousel/head-on-carousel';
import ProductAccordion from '../../../components/molecules/product-accordion/product-accordion';
import BookAnAppointment from '../../../components/molecules/book-an-appointment/book-an-appointment';
import { brandColors } from '../../../constants/colors';
import SectionContainer from '../../../components/atoms/section-container/section-container';
import { getAllProducts, getProductDetails } from '../../../services/products';
import ProductTabs from '../../../components/organisms/product-tabs/product-tabs';
import CardModal from '../../../components/atoms/card-modal/card-modal';
import TabTitle from '../../../components/atoms/tab-title/tab-title';
import { StrapiBrandType, StrapiBrandWebsiteSlug } from '../../../enums/strapi';

type IndexProps = {
  brandPageContents: BrandsContentResponse;
  productDetails: ProductsModel;
};

export function Index(props: IndexProps) {
  const { productDetails } = props;
  const { attributes } = productDetails;
  const router = useRouter();

  const productEmailLink = generateProductEmailLink(
    attributes.ModelNumber,
    router?.query?.['product'] as string
  );

  const [activeTab, setActiveTab] = useState('FEATURES');
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery(MediaQuery.SM);
  const isTablet = useMediaQuery(MediaQuery.MD);
  const imageHeight = isMobile ? 150 : isTablet ? 100 : 60;

  function switchTab(tabName: string) {
    setActiveTab(tabName);
  }

  function handleOpenModal() {
    setIsOpen(true);
  }

  const { SUBZERO_HEX, WOLF_HEX } = brandColors;

  const brandColor = isEqual(attributes.Brand, SUBZERO_API_NAME)
    ? SUBZERO_HEX
    : WOLF_HEX;

  const hoverColor = isEqual(attributes.Brand, SUBZERO_API_NAME)
    ? 'hover:text-primaryBlue'
    : 'hover:text-primaryRed';

  const featureCardsContent = attributes.ProductFeatures?.map((feature) => {
    return {
      description: feature.LongDescription,
      hoverColor: brandColor,
      heading: feature.heading,
      image: feature.image
    };
  });

  const headOnImages = attributes.HeadOnImages.data.map(
    (headOnImage) => headOnImage.attributes.url
  );

  const breadcrumbItems = [
    {
      tabLabel: isEqual(attributes.Brand, SUBZERO_API_NAME)
        ? SUBZERO_BRAND_NAME
        : WOLF_BRAND_NAME,
      route: isEqual(attributes.Brand, SUBZERO_API_NAME)
        ? `/${SUBZERO_SLUG}`
        : `/${WOLF_SLUG}`
    },
    {
      tabLabel: queryToString(attributes.Category),
      route: isEqual(attributes.Brand, SUBZERO_API_NAME)
        ? `/${SUBZERO_SLUG}/${attributes.Category}`
        : `/${WOLF_SLUG}/${attributes.Category}`
    },
    {
      tabLabel: attributes.ModelNumber,
      route: '#'
    }
  ];

  return (
    <>
      <TabTitle page={attributes.ModelNumber} />
      <div>
        <DynamicBreadcrumbs
          hoverColor={hoverColor}
          breadcrumbItems={breadcrumbItems}
          position={`pl-6 pt-6 lg:pl-20 lg:pt-12`}
        />
      </div>
      <SectionContainer className="flex flex-col lg:flex-row">
        <div className="w-full h-full lg:w-1/2 lg:px-12">
          <HeadOnCarousel
            headOnImages={attributes.HeadOnImages.data}
            swiperColor={brandColor}
            handleOpenModal={handleOpenModal}
          />
          <CardModal
            isOpen={isOpen}
            imageHeight={imageHeight}
            src={headOnImages}
            setIsOpen={setIsOpen}
          />
        </div>
        <div className="mt-24 lg:mt-0">
          <div>
            <p className="text-primaryGray text-body">
              {attributes.ModelNumber}
            </p>
            <p className="text-header6 lg:text-header5 uppercase font-MuseoSans500 mt-2">
              {attributes.ModelName}
            </p>
          </div>
          <div className="mt-4">
            <ProductAccordion productDetails={productDetails} />
          </div>
        </div>
      </SectionContainer>
      <div className="lg:mt-24">
        <div className="flex justify-center space-x-4 h-12 lg:h-20">
          <ProductTabButton
            activeTab={activeTab}
            tabLabel={ProductContentTabs.FEATURES}
            switchTab={() => switchTab('FEATURES')}
          />
          <ProductTabButton
            activeTab={activeTab}
            tabLabel={ProductContentTabs.IMAGE_GALLERY}
            switchTab={() => switchTab('IMAGE GALLERY')}
          />
          <ProductTabButton
            activeTab={activeTab}
            tabLabel={ProductContentTabs.DOWNLOADS}
            switchTab={() => switchTab('DOWNLOADS')}
          />
        </div>
        <div className="bg-headerBackground/50 h-full">
          <ProductTabs
            activeTab={activeTab}
            downloads={attributes.Downloads}
            featureCardsContent={featureCardsContent}
            heroImages={attributes.HeroImages.data}
            swiperColor={brandColor}
          />
        </div>
        <div className="bg-iconsBackground h-full py-12 flex flex-wrap justify-center items-center lg:space-y-0 space-x-4 lg:space-x-12 px-4">
          <ImageHoverButton
            buttonLabel="CALL US >"
            ctaLink={linkRoutes.CALL}
            hoverImage="/icons/call-hover-icon.jpeg"
            inactiveImage="/icons/call-inactive.jpeg"
          />
          <ImageHoverButton
            buttonLabel="EMAIL US >"
            ctaLink={productEmailLink}
            hoverImage="/icons/email-inactive.jpeg"
            inactiveImage="/icons/email-hover-icon.jpeg"
          />
          <ImageHoverButton
            buttonLabel="VISIT OUR SHOWROOM >"
            ctaLink={pageRoute.ABOUT_SHOWROOM_LOCATIONS}
            hoverImage="/icons/szwf-inactive.jpeg"
            inactiveImage="/icons/szwf-hover-icon.jpeg"
          />
        </div>
      </div>
      <BookAnAppointment />
    </>
  );
}

export async function getStaticProps({ params }) {
  const { product } = params;

  const productDetails = await getProductDetails(product);

  return {
    props: { productDetails },
    revalidate: 300
  };
}

export async function getStaticPaths() {
  const products = await getAllProducts();

  const paths = products.map((product) => {
    const brand = isEqual(
      product.attributes.Brand,
      StrapiBrandType.WOLF_COOKING
    )
      ? StrapiBrandWebsiteSlug.WOLF_COOKING
      : StrapiBrandWebsiteSlug.SUBZERO_REFRIGERATION;

    return {
      params: {
        brand: brand,
        product: product.attributes.websiteSlug
      }
    };
  });

  return {
    paths: paths,
    fallback: false
  };
}

export default Index;
